import type { ProductionAsset, Shot } from '@/api/studio-production'
import { assetMediaCategory } from '@/features/studio/pages/assets/asset-utils'
import { assetMediaUrl } from '@/features/studio/lib/media-display'
import { collectSceneReferenceUrls } from '@/features/studio/lib/scene-reference-assets'
import {
  composeSelectedShotsCanvasPrompt,
  shotPublicIdsKey,
} from '@/features/studio/lib/shot-canvas-prompt'
import { createStudioNode, type CanvasLaunchContext } from '@/features/studio/canvas/utils'
import type { HandleDataType, StudioFlowEdge, StudioFlowNode } from '@/features/studio/canvas/types'

const ASSET_X = 80
const CONTEXT_X = 360
const ROW_START_Y = 80
const UPLOAD_ROW_H = 168
const BATCH_CONTEXT_H = 360

export interface WorkbenchDuplicateInfo {
  batchExists: boolean
  contextNodeId?: string
}

export interface WorkbenchMergeOptions {
  replaceDuplicates: boolean
}

export function hasSeededSceneAssets(nodes: StudioFlowNode[], sceneScopeKey: string): boolean {
  return nodes.some(
    (n) =>
      n.data.nodeType === 'upload' &&
      n.data.upload?.seededFromWorkbench &&
      n.data.upload?.sceneScopeKey === sceneScopeKey,
  )
}

export function detectWorkbenchDuplicates(
  nodes: StudioFlowNode[],
  shots: Shot[],
): WorkbenchDuplicateInfo {
  const selectedKey = shotPublicIdsKey(shots.map((s) => s.public_id))

  for (const n of nodes) {
    if (n.data.nodeType !== 'context' || n.data.context?.ctx !== 'shot-frame') continue
    const ids = n.data.context.shotPublicIds
    if (ids?.length && shotPublicIdsKey(ids) === selectedKey) {
      return { batchExists: true, contextNodeId: n.id }
    }
  }

  return { batchExists: false }
}

function maxContextBottomY(nodes: StudioFlowNode[]): number {
  let max = ROW_START_Y
  for (const n of nodes) {
    if (n.data.nodeType !== 'context') continue
    max = Math.max(max, n.position.y + BATCH_CONTEXT_H)
  }
  return max
}

function refImageLabels(urlCount: number): string[] {
  return Array.from({ length: urlCount }, (_, i) => `图片${i + 1}`)
}

function resolveMimeType(asset: ProductionAsset): string {
  if (asset.mime_type?.trim()) return asset.mime_type.trim()
  const cat = assetMediaCategory(asset)
  if (cat === 'video') return 'video/mp4'
  if (cat === 'audio') return 'audio/mpeg'
  return 'image/jpeg'
}

function outputDataTypeForAsset(asset: ProductionAsset): HandleDataType {
  const cat = assetMediaCategory(asset)
  if (cat === 'video') return 'video'
  if (cat === 'audio') return 'audio'
  return 'image'
}

function sceneAssetsForSeed(
  assets: ProductionAsset[],
  legacyStoryboardUrl?: string,
): ProductionAsset[] {
  const items = assets.filter((a) => !!assetMediaUrl(a)?.trim())
  const legacy = legacyStoryboardUrl?.trim()
  if (legacy && !items.some((a) => assetMediaUrl(a) === legacy)) {
    items.unshift({
      id: 'legacy-storyboard',
      name: 'Scene board',
      kind: 'storyboard',
      status: 'ready',
      url: legacy,
      media_url: legacy,
      mime_type: 'image/jpeg',
    })
  }
  return items
}

function createSceneUploadNode(
  asset: ProductionAsset,
  index: number,
  sceneScopeKey: string,
): StudioFlowNode {
  const url = assetMediaUrl(asset)!
  const mime = resolveMimeType(asset)
  const dataType = outputDataTypeForAsset(asset)
  const label = asset.name?.trim() || (dataType === 'video' ? `视频 ${index + 1}` : `图片 ${index + 1}`)
  return createStudioNode('upload', { x: ASSET_X, y: ROW_START_Y + index * UPLOAD_ROW_H }, {
    label,
    upload: {
      url,
      mime,
      filename: asset.name,
      assetPublicId: asset.public_id ?? (asset.id.startsWith('canvas:') ? undefined : asset.id),
      sceneScopeKey,
      seededFromWorkbench: true,
    },
    output: {
      status: 'succeeded',
      previewUrl: url,
      dataType,
    },
  })
}

function createBatchContextNode(
  shots: Shot[],
  sceneLabel: string,
  sceneHeading: string | undefined,
  y: number,
  refLabels: string[],
): StudioFlowNode {
  const { title, prompt } = composeSelectedShotsCanvasPrompt({
    shots,
    sceneLabel,
    sceneHeading,
    refImageLabels: refLabels,
  })
  return createStudioNode('context', { x: CONTEXT_X, y }, {
    label: title,
    context: {
      ctx: 'shot-frame',
      shotPublicIds: shots.map((s) => s.public_id),
      shotSlug: title,
      title,
      prompt,
      intent: 'selection',
    },
  })
}

export function updateContextNodePrompt(node: StudioFlowNode, prompt: string, title?: string): StudioFlowNode {
  if (node.data.nodeType !== 'context' || !node.data.context) return node
  return {
    ...node,
    data: {
      ...node.data,
      label: title ?? node.data.label,
      context: {
        ...node.data.context,
        title: title ?? node.data.context.title,
        prompt,
      },
    },
  }
}

export function mergeWorkbenchLaunchIntoGraph(input: {
  launch: CanvasLaunchContext
  shots: Shot[]
  sceneHeading?: string
  sceneAssets: ProductionAsset[]
  legacyStoryboardUrl?: string
  existingNodes: StudioFlowNode[]
  options: WorkbenchMergeOptions
}): { nodes: StudioFlowNode[]; edges: StudioFlowEdge[]; updatedNodes: StudioFlowNode[] } {
  const nodesToAdd: StudioFlowNode[] = []
  const sceneScopeKey = input.launch.scopeKey
  const seedAssets = sceneAssetsForSeed(input.sceneAssets, input.legacyStoryboardUrl)
  const refUrlCount = collectSceneReferenceUrls(input.sceneAssets, input.legacyStoryboardUrl).length
  const labels = refImageLabels(refUrlCount)

  let workingNodes = input.existingNodes.filter(
    (n) =>
      !(
        n.data.nodeType === 'ref_scene' &&
        n.data.refScene?.seededFromWorkbench &&
        n.data.refScene?.sceneScopeKey === sceneScopeKey
      ),
  )

  if (!hasSeededSceneAssets(workingNodes, sceneScopeKey) && seedAssets.length > 0) {
    seedAssets.forEach((asset, index) => {
      nodesToAdd.push(createSceneUploadNode(asset, index, sceneScopeKey))
    })
  }

  const dupes = detectWorkbenchDuplicates(workingNodes, input.shots)
  let nextY = maxContextBottomY([...workingNodes, ...nodesToAdd])

  if (dupes.batchExists && dupes.contextNodeId) {
    if (input.options.replaceDuplicates) {
      const { title, prompt } = composeSelectedShotsCanvasPrompt({
        shots: input.shots,
        sceneLabel: input.launch.label,
        sceneHeading: input.sceneHeading,
        refImageLabels: labels,
      })
      workingNodes = workingNodes.map((n) =>
        n.id === dupes.contextNodeId ? updateContextNodePrompt(n, prompt, title) : n,
      )
    }
  } else if (!dupes.batchExists && input.shots.length > 0) {
    nodesToAdd.push(
      createBatchContextNode(
        input.shots,
        input.launch.label,
        input.sceneHeading,
        nextY,
        labels,
      ),
    )
  }

  const updatedNodes = workingNodes.filter((n) => {
    const orig = input.existingNodes.find((o) => o.id === n.id)
    return orig && JSON.stringify(orig.data.context) !== JSON.stringify(n.data.context)
  })

  return { nodes: nodesToAdd, edges: [], updatedNodes }
}

export function hasWorkbenchDuplicates(info: WorkbenchDuplicateInfo): boolean {
  return info.batchExists
}
